import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { UserRole, Permission, PipelineType, AnalyticsPageType } from 'src/utils/enumerations';

import { DashboardLayout } from 'src/layout';
import { AuthGuard, RoleBasedGuard } from 'src/auth';
import {
  SalesSummaryContext,
  SummaryContextProvider,
  ServicingSummaryContext,
  ComplianceSummaryContext,
} from 'src/context/summary/SummaryContext';

import { LoadingScreen } from 'src/components/loading-screen';
import { PipelineProvider } from 'src/components/upload-table';

import { usePathname } from '../hooks';

const HomePage = lazy(() => import('src/pages/HomePage'));
const SummaryPage = lazy(() => import('src/pages/SummaryPage'));
const SalesAnalyticsPage = lazy(() => import('src/pages/sales/analytics/SalesAnalyticsPage'));
const SearchPage = lazy(() => import('src/pages/sales/SearchPage'));

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

const SalesPipelineWrapper = () => (
  <PipelineProvider pipelineType={PipelineType.Sales}>
    <SummaryContextProvider context={SalesSummaryContext}>
      <Outlet />
    </SummaryContextProvider>
  </PipelineProvider>
);

const CompliancePipelineWrapper = () => (
  <PipelineProvider pipelineType={PipelineType.Hmda}>
    <SummaryContextProvider context={ComplianceSummaryContext}>
      <Outlet />
    </SummaryContextProvider>
  </PipelineProvider>
);

const ServicingPipelineWrapper = () => (
  <PipelineProvider pipelineType={PipelineType.Servicing}>
    <SummaryContextProvider context={ServicingSummaryContext}>
      <Outlet />
    </SummaryContextProvider>
  </PipelineProvider>
);

export const dashboardRoutes = [
  {
    path: '/',
    element: <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      {
        path: '/sales',
        element: (
          <RoleBasedGuard
            requiredRoles={[UserRole.Sales, UserRole.SalesManager, UserRole.SuperUser]}
          >
            <SalesPipelineWrapper />
          </RoleBasedGuard>
        ),
        children: [
          { path: '', element: <HomePage /> },
          { path: 'summary/:id', element: <SummaryPage /> },
          {
            path: 'analytics',
            element: (
              <RoleBasedGuard requiredPermissions={[Permission.ADMINView, Permission.SMView]}>
                <Outlet />
              </RoleBasedGuard>
            ),
            children: [
              { index: true, element: <SalesAnalyticsPage /> },
              {
                path: 'manager/:managerId',
                element: <SalesAnalyticsPage pageType={AnalyticsPageType.SalesManager} />,
              },
              {
                path: 'manager/:managerId/loan-advisor/:loanAdvisorId',
                element: <SalesAnalyticsPage pageType={AnalyticsPageType.LoanAdvisor} />,
              },
            ],
          },
          { path: 'search', element: <SearchPage /> },
        ],
      },
      {
        path: '/compliance',
        element: (
          <RoleBasedGuard requiredRoles={[UserRole.Compliance]}>
            <CompliancePipelineWrapper />
          </RoleBasedGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard requiredPermissions={[Permission.ComplianceUploadView]}>
                <HomePage />
              </RoleBasedGuard>
            ),
          },
          { path: 'summary/:id', element: <SummaryPage /> },
        ],
      },
      {
        path: '/servicing',
        element: (
          <RoleBasedGuard requiredRoles={[UserRole.Servicing]}>
            <ServicingPipelineWrapper />
          </RoleBasedGuard>
        ),
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard requiredPermissions={[Permission.ServicingUploadView]}>
                <HomePage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'summary/:id',
            element: <SummaryPage />,
          },
        ],
      },
    ],
  },
];
